import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Table, Form } from "react-bootstrap";
import { Button } from "bootstrap-4-react/lib/components";
import "./AddToMarketplaceModal.css";

/**
 * BulkAddToMarketplaceModal
 *
 * Step-1 popup on the Inventory screen for the bulk "Add to Marketplace" flow.
 * Renders the current inventory products with a checkbox per row so the seller
 * can select several products at once. On Submit, the parent receives the
 * selected products and opens the step-2 accordion modal (BulkEbayListingModal)
 * with one eBay listing form per selected product.
 */
const BulkAddToMarketplaceModal = ({ show, onHide, products, onSubmit }) => {
    const [selectedIds, setSelectedIds] = useState([]);
    const [searchText, setSearchText] = useState("");

    // Reset selection whenever the modal is re-opened so a previous run
    // doesn't leak into a fresh selection.
    useEffect(() => {
        if (show) {
            setSelectedIds([]);
            setSearchText("");
        }
    }, [show]);

    const productKey = (p) => p?.id ?? p?.Id;

    const filteredProducts = useMemo(() => {
        const text = searchText.trim().toLowerCase();
        if (!text) return products || [];
        return (products || []).filter((p) => {
            const sku = String(p.productSku || "").toLowerCase();
            const name = String(p.productName || "").toLowerCase();
            return sku.includes(text) || name.includes(text);
        });
    }, [products, searchText]);

    const allVisibleSelected =
        filteredProducts.length > 0 &&
        filteredProducts.every((p) => selectedIds.includes(productKey(p)));

    const toggleOne = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const toggleAll = () => {
        const visibleIds = new Set(filteredProducts.map(productKey));
        if (allVisibleSelected) {
            setSelectedIds((prev) => prev.filter((id) => !visibleIds.has(id)));
        } else {
            setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
        }
    };

    const handleSubmit = () => {
        const selected = (products || []).filter((p) => selectedIds.includes(productKey(p)));
        if (!selected.length) return;
        onSubmit(selected);
    };

    const handleClose = () => {
        setSelectedIds([]);
        setSearchText("");
        onHide();
    };

    return (
        <Modal
            show={show}
            size="lg"
            centered
            onHide={handleClose}
            aria-labelledby="bulk-add-marketplace-title"
            className="generateKey modalDeleteWrapper"
        >
            <Modal.Header closeButton>
                <Modal.Title id="bulk-add-marketplace-title">
                    Add Products to Marketplace
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Control
                        type="text"
                        placeholder="Search by SKU or name"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ maxWidth: 320 }}
                    />
                    <span className="text-muted" style={{ fontSize: 13 }}>
                        {selectedIds.length} selected
                    </span>
                </div>
                <div className="tableFixHead" style={{ maxHeight: 400, overflowY: "auto" }}>
                    <Table className="tableWide">
                        <thead className="tbl-btch">
                            <tr className="table-rw-batch">
                                <th width="5%">
                                    <Form.Check
                                        type="checkbox"
                                        checked={allVisibleSelected}
                                        onChange={toggleAll}
                                        aria-label="Select all"
                                    />
                                </th>
                                <th width="10%">Thumbnail</th>
                                <th width="20%">SKU</th>
                                <th width="35%">Product Name</th>
                                <th width="15%">Quantity</th>
                            </tr>
                        </thead>
                        <tbody className="tbl-bt-body">
                            {filteredProducts.map((p) => {
                                const id = productKey(p);
                                const isChecked = selectedIds.includes(id);
                                return (
                                    <tr key={id}>
                                        <td>
                                            <Form.Check
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => toggleOne(id)}
                                                aria-label={`Select ${p.productName || p.productSku}`}
                                            />
                                        </td>
                                        <td>
                                            {p.imageUrl ? (
                                                <img
                                                    src={p.imageUrl}
                                                    alt=""
                                                    style={{ height: 24 }}
                                                />
                                            ) : null}
                                        </td>
                                        <td>{p.productSku}</td>
                                        <td>{p.productName}</td>
                                        <td>
                                            {p.totalQuantity === 0
                                                ? p.quantity
                                                : Number(p.totalQuantity ?? p.quantity ?? 0)}
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredProducts.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center text-muted">
                                        No products to display.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button className="cus-primary-transparent-btn" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    className="cus-seconday-bg-btn"
                    onClick={handleSubmit}
                    disabled={selectedIds.length === 0}
                >
                    Submit ({selectedIds.length})
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

BulkAddToMarketplaceModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    products: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            productSku: PropTypes.string,
            productName: PropTypes.string,
            imageUrl: PropTypes.string,
            quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            totalQuantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        })
    ),
};

BulkAddToMarketplaceModal.defaultProps = {
    products: [],
};

export default BulkAddToMarketplaceModal;
